import { Tag } from 'antd';
import type { ReactNode } from 'react';
import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  FormOutlined,
  HourglassOutlined,
  MinusCircleOutlined,
  StopOutlined,
  SyncOutlined,
} from '@ant-design/icons';

/**
 * 场景（``scene_inspection``）状态可视化 Tag。
 *
 * 与后端 ``SceneStatusEnum`` 一一对齐：ENTRYING(1) / QUALITY_CHECKING(5) /
 * COMPLETED_DEFECTIVE(7) / COMPLETED(8) / INVALIDATED(9)。注意:场景生命周期跟
 * Job/Task 状态机完全不同,不要与 JobStatusTag 混用——两者 id 命名空间不同会撞号。
 *
 * 颜色映射哲学：
 * - **进行中**（质检中）→ processing 蓝（带 spin 表示活动中）
 * - **阶段成功**（已录入）→ cyan 青
 * - **最终成功**（已完成）→ success 绿
 * - **合格但有瑕疵**（已完成-瑕疵品）→ warning 黄（下游行为等同已完成,仅提示存在瑕疵）
 * - **不合格**（INVALIDATED）→ default 灰
 */

interface StatusVisual {
  color: string;
  icon: ReactNode;
  /** 后端 statusName 缺失时的兜底中文 */
  fallbackText: string;
}

const STATUS_VISUAL: Record<number, StatusVisual> = {
  1: { color: 'cyan',       icon: <FormOutlined />,                fallbackText: '已录入' },
  5: { color: 'processing', icon: <SyncOutlined spin />,           fallbackText: '质检中' },
  7: { color: 'warning',    icon: <ExclamationCircleOutlined />,   fallbackText: '已完成-瑕疵品' },
  8: { color: 'success',    icon: <CheckCircleOutlined />,         fallbackText: '已完成' },
  9: { color: 'default',    icon: <StopOutlined />,                fallbackText: '不合格' },
};

/** 中文 statusName → id 反查；让只有 statusName 没有 status id 的接口（如
 * ``GET /scene-inspection/available-scenes``）也能正常着色。
 * 自动从 STATUS_VISUAL 派生，避免与正向表手抖不一致。
 */
const NAME_TO_ID: Record<string, number> = Object.fromEntries(
  Object.entries(STATUS_VISUAL).map(([id, v]) => [v.fallbackText, Number(id)]),
);

const FALLBACK_VISUAL: StatusVisual = {
  color: 'default',
  icon: <MinusCircleOutlined />,
  fallbackText: '',
};

interface SceneStatusTagProps {
  /** 后端 SceneStatusEnum.id */
  status?: number | null;
  /** 后端返回的 ``statusName``；缺失时按 enum 兜底，仍缺则 "-" */
  statusName?: string | null;
  /** Tag 内左侧是否显示图标，默认 true */
  showIcon?: boolean;
  /** Tag 最小宽度（px），用于在表格列里把不同长度的状态对齐显示。
   *  传 0/null 可关闭固定宽度，让 Tag 按内容收缩。 */
  minWidth?: number | null;
}

/** 默认最小宽度 = 4 个中文字 + icon + 内边距，覆盖 ``计算成功`` / ``质检驳回`` 这类最长文案。 */
const DEFAULT_MIN_WIDTH = 88;

export default function SceneStatusTag({
  status,
  statusName,
  showIcon = true,
  minWidth = DEFAULT_MIN_WIDTH,
}: SceneStatusTagProps) {
  if (!statusName && (status === null || status === undefined)) {
    return <span style={{ color: 'rgba(0,0,0,0.45)' }}>-</span>;
  }

  // 优先用 status id；没有时按中文 statusName 反查（兼容只返回 statusName 的接口）
  const effectiveId =
    typeof status === 'number' ? status : statusName ? NAME_TO_ID[statusName] : undefined;
  const visual =
    typeof effectiveId === 'number' ? STATUS_VISUAL[effectiveId] || FALLBACK_VISUAL : FALLBACK_VISUAL;
  const text = statusName || visual.fallbackText || `未知(${status})`;

  // 当 statusName 缺且 enum 也未声明的极端情况：兜底用 HourglassOutlined 表示"待定"
  const icon = showIcon ? (visual.icon || <HourglassOutlined />) : undefined;

  return (
    <Tag
      color={visual.color}
      icon={icon}
      style={{
        marginInlineEnd: 0,
        minWidth: minWidth ?? undefined,
        textAlign: 'center',
      }}
    >
      {text}
    </Tag>
  );
}
